import apiClient from "../server";

export const getAllNews = async (params = {}) => {
  try {
    console.log('📥 [NewsServer] getAllNews called with params:', params);
    // Use backoffice endpoint to get all news items (including empty data arrays)
    const response = await apiClient.get(`news/backoffice`, { params });
    console.log('📥 [NewsServer] getAllNews response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ [NewsServer] Error getting news:', error);
    console.error('❌ [NewsServer] Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || "Error getting news");
  }
};

export const createNews = async (data) => {
  try {
    console.log('📤 [NewsServer] createNews called with data:', data);
    // Use backoffice endpoint for creating news
    const response = await apiClient.post(`news/backoffice`, data);
    console.log('📤 [NewsServer] createNews response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ [NewsServer] Create news error:', error);
    console.error('❌ [NewsServer] Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || "Error creating news");
  }
};

export const deleteNews = async (id) => {
  try {
    console.log('🗑️ [NewsServer] deleteNews called with id:', id);
    // Use backoffice endpoint for deleting news
    const response = await apiClient.delete(`news/backoffice/${id}`);
    console.log('🗑️ [NewsServer] deleteNews response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ [NewsServer] Delete news error:', error);
    console.error('❌ [NewsServer] Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || "Error deleting news");
  }
};

export const getAllProfiles = async (cityId) => {
  try {
    const response = await apiClient.get(`profiles/all?pagination=false${cityId ? `&city=${cityId}` : ''}`);
    return response.data;
  } catch (error) {
    throw new Error("Error getting profiles");
  }
};

export const updateNews = async (id, data) => {
  try {
    console.log('🔄 [NewsServer] updateNews called with id:', id, 'data:', data);
    // Use backoffice endpoint for updating news
    const response = await apiClient.patch(`news/backoffice/update/${id}`, data);
    console.log('🔄 [NewsServer] updateNews response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ [NewsServer] Update news error:', error);
    console.error('❌ [NewsServer] Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || "Error updating news");
  }
};
export const updateProfileOrder = async (newsId, profileIds) => {
  try {
    const response = await apiClient.patch(`news/profiles/order/${newsId}`, { profileIds });
    return response.data;
  } catch (error) {
    throw new Error("Error updating profile order");
  }
};

export const toggleNewsEnabled = async (id, data) => {
  try {
    console.log('🔄 [NewsServer] toggleNewsEnabled called with id:', id, 'data:', data);
    // Use backoffice endpoint for toggling news enabled status
    const response = await apiClient.patch(`news/backoffice/update/${id}`, data);
    console.log('🔄 [NewsServer] toggleNewsEnabled response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ [NewsServer] Toggle news enabled error:', error);
    console.error('❌ [NewsServer] Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || "Error toggling news enabled");
  }
};

export const getNewsProfiles=async(newsId)=>{
  try{
    const response=await apiClient.get(`news/profiles/${newsId}`);
    return response.data;
  }catch(error){
    throw new Error("Error fetching news profiles");
  }
}

export const deleteProfileFromNews = async (newsId, profileId) => {
  try {
    console.log('🗑️ [NewsServer] deleteProfileFromNews called with:', { newsId, profileId });
    return await removeContentFromNews(newsId, profileId);
  } catch (error) {
    console.error('❌ [NewsServer] Error deleting profile from news:', error);
    throw error;
  }
};

// Generic function to remove any content (profile or publication) from news
export const removeContentFromNews = async (newsId, contentId) => {
  try {
    console.log('🗑️ [NewsServer] removeContentFromNews called with:', { newsId, contentId });
    
    // First get the current news item to get its data array
    const currentNewsResponse = await getAllNews({ cityId: null }); // Get all news to find this item
    const currentNews = currentNewsResponse?.data?.find(news => news._id === newsId);
    
    if (!currentNews) {
      throw new Error('News item not found');
    }
    
    console.log('🗑️ [NewsServer] Current news data array:', currentNews.data);
    
    // Remove the content ID from the data array
    const updatedData = Array.isArray(currentNews.data) 
      ? currentNews.data.filter(id => id !== contentId)
      : [];
    
    console.log('🗑️ [NewsServer] Updated data array:', updatedData);
    
    // Update the news item with the new data array using backoffice endpoint
    const response = await apiClient.patch(`news/backoffice/update/${newsId}`, {
      data: updatedData
    });
    
    console.log('🗑️ [NewsServer] Remove content response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ [NewsServer] Error removing content from news:', error);
    console.error('❌ [NewsServer] Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || "Error removing content from news");
  }
};

// Function to remove publication from news (alias for removeContentFromNews)
export const deletePublicationFromNews = async (newsId, publicationId) => {
  try {
    console.log('🗑️ [NewsServer] deletePublicationFromNews called with:', { newsId, publicationId });
    return await removeContentFromNews(newsId, publicationId);
  } catch (error) {
    console.error('❌ [NewsServer] Error deleting publication from news:', error);
    throw error;
  }
};

export const addNewsProfile = async (newsId, body) => {
  try {
    console.log('➕ [NewsServer] addNewsProfile called with:', { newsId, body });
    const contentId = body.profileId || body.publicationId || body.id;
    return await addContentToNews(newsId, contentId);
  } catch (error) {
    console.error('❌ [NewsServer] Error adding profile to news:', error);
    throw error;
  }
};

// Generic function to add any content (profile or publication) to news
export const addContentToNews = async (newsId, contentId) => {
  try {
    console.log('➕ [NewsServer] addContentToNews called with:', { newsId, contentId });
    
    if (!contentId) {
      throw new Error('No content ID provided');
    }
    
    // First get the current news item to get its data array
    const currentNewsResponse = await getAllNews({ cityId: null }); // Get all news to find this item
    const currentNews = currentNewsResponse?.data?.find(news => news._id === newsId);
    
    if (!currentNews) {
      throw new Error('News item not found');
    }
    
    console.log('➕ [NewsServer] Current news data array:', currentNews.data);
    
    // Add the content ID to the data array (avoid duplicates)
    const currentData = Array.isArray(currentNews.data) ? currentNews.data : [];
    const updatedData = currentData.includes(contentId) 
      ? currentData 
      : [...currentData, contentId];
    
    console.log('➕ [NewsServer] Updated data array:', updatedData);
    
    // Update the news item with the new data array using backoffice endpoint
    const response = await apiClient.patch(`news/backoffice/update/${newsId}`, {
      data: updatedData
    });
    
    console.log('➕ [NewsServer] Add content response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ [NewsServer] Error adding content to news:', error);
    console.error('❌ [NewsServer] Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || "Error adding content to news");
  }
};

// Function to add publication to news (alias for addContentToNews)
export const addNewsPublication = async (newsId, body) => {
  try {
    console.log('➕ [NewsServer] addNewsPublication called with:', { newsId, body });
    const contentId = body.publicationId || body.profileId || body.id;
    return await addContentToNews(newsId, contentId);
  } catch (error) {
    console.error('❌ [NewsServer] Error adding publication to news:', error);
    throw error;
  }
};

export const getAllMusicTypes = async (params = {}) => {
  try {
    const response = await apiClient.get(`params/music-types/all`, { params });
    return response.data;
  } catch (error) {
    throw new Error("Error getting music types");
  }
};

export const createMusicType = async (data) => {
  try {
    const response = await apiClient.post(`params/music-types/create`, data);
    return response.data;
  } catch (error) {
    throw new Error("Error creating music type");
  }
};

export const updateMusicType = async (id, data) => {
  try {
    const response = await apiClient.patch(`params/music-types/update/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error("Error updating music type");
  }
};

export const deleteMusicType = async (id) => {
  try {
    const response = await apiClient.delete(`params/music-types/delete/${id}`);
    return response.data;
  } catch (error) {
    throw new Error("Error deleting music type");
  }
};

// Subsection API
export const getAllSubsections = async (params = {}) => {
  try {
    const response = await apiClient.get(`params/subSections/all`, { params });
    return response.data;
  } catch (error) {
    throw new Error("Error getting subsections");
  }
};

export const createSubsection = async (data) => {
  try {
    const response = await apiClient.post(`params/subSections/create`, data);
    return response.data;
  } catch (error) {
    throw new Error("Error creating subsection");
  }
};

export const updateSubsection = async (id, data) => {
  try {
    const response = await apiClient.patch(`params/subSections/update/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error("Error updating subsection");
  }
};

export const deleteSubsection = async (id) => {
  try {
    const response = await apiClient.delete(`params/subSections/delete/${id}`);
    return response.data;
  } catch (error) {
    throw new Error("Error deleting subsection");
  }
};